// Matterport lead gate — the public-share lead-capture overlay for the Digital
// Twin (Matterport) experience. Self-contained: drop it over the MP iframe,
// feed it the live sweep count, and it arms / shows / captures / unlocks on its
// own. Mirrors the meta-model engine's behaviour (pure "3 points", 30-day
// unlock) and the same 3-API flow (check-status -> send-otp -> verify-otp).
//
// UX intent: warm and welcoming, never infringing. A soft anticipation teaser
// appears first so the gate is expected, not a cold pop-up; the scene stays
// alive (frosted) behind; the ask is framed as an invitation; and unlocking is
// a celebrated "you've earned it" moment. Wrong OTP shakes + reddens rather
// than scolding.
//
// ONLY renders when `enabled` is true (the public share view / leadgate=1) —
// it never appears on the authenticated platform. All decision logic lives in
// ./leadGateFlow (pure, unit-tested); this is the view + orchestration.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { checkLeadStatus, sendLeadOtp, verifyLeadOtp, type LeadCtx } from "./leadGateApi";
import { getActiveUnlock, getDeviceId, writeUnlock } from "./leadGateStorage";
import {
  evaluateTrigger,
  interpretCheckStatus,
  interpretVerify,
  validateName,
  validateOtp,
  validatePhone,
  validateEmail,
  RESEND_COOLDOWN_S,
} from "./leadGateFlow";

export interface MatterportLeadGateProps {
  /** Arm only on the public share view (leadgate=1). Agent-internal stays off. */
  enabled: boolean;
  agencyId: string;
  /** Namespaces the 30-day unlock record. Slug preferred, id is fine. */
  projectKey: string;
  projectName?: string;
  /** Personalisation (all available from the share-link click response). */
  sharerName?: string;
  agencyName?: string;
  agencyLogoUrl?: string;
  /** Binds the captured lead to the VR session for attribution. */
  sessionId?: string;
  /** Website embed key — sent with the OTP calls so the lead is attributed to the embed and its site. */
  embedKey?: string;
  /** The visitor's own campaign tags from the host page. Without these a session-less
   *  embed files a paid click as organic — see LeadCtx.utm. */
  utm?: Record<string, string>;
  /** Live count of distinct sweeps entered (from useMatterportAnalytics). */
  sweepCount: number;
  /** Optional — enables the "3/10" ring + "7 more spaces" FOMO line. */
  totalSpaces?: number;
  /** Trigger tuning. Defaults mirror MM "3 points" + sane guards. */
  nodeTarget?: number; // default 3
  minDwellMs?: number; // default 10_000
  maxWaitMs?: number; // default 30_000
  /**
   * Start the dwell clock at mount instead of waiting for a session or a sweep.
   * For experiences that emit NEITHER — a meta model in a cross-origin iframe —
   * where the gate would otherwise never arm. Pair it with `maxWaitMs`.
   */
  armOnMount?: boolean;
  /** Fired once when the experience unlocks (fresh capture OR existing unlock). */
  onUnlock?: (info: { customerId: string }) => void;
}

type Step = "phone" | "name" | "otp" | "success";

const GOLD = "#E8B96B";
const GOLD_SOFT = "#F0C674";

const STYLE = `
.mlg-overlay{position:fixed;inset:0;z-index:2147483000;display:flex;overflow-y:auto;padding:16px;
  background:rgba(8,11,20,.55);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);
  transition:opacity .5s ease;opacity:1;animation:mlg-fade .45s ease both;}
.mlg-overlay.mlg-leaving{opacity:0;}
.mlg-card{margin:auto;position:relative;width:100%;max-width:380px;border-radius:24px;padding:26px 22px 20px;
  background:linear-gradient(180deg,rgba(33,30,66,.97),rgba(18,18,36,.98));
  border:1px solid rgba(232,185,107,.26);
  box-shadow:0 30px 80px -20px rgba(0,0,0,.7),0 0 60px -28px rgba(232,185,107,.5),0 0 0 1px rgba(255,255,255,.03) inset;
  color:#E7E9F2;animation:mlg-pop .5s cubic-bezier(.18,.9,.32,1.2) both;}
.mlg-card::before{content:"";position:absolute;inset:0;border-radius:24px;pointer-events:none;
  background:radial-gradient(120% 80% at 50% -10%,rgba(232,185,107,.14),transparent 60%);}
.mlg-logo{height:22px;width:auto;object-fit:contain;opacity:.95;}
.mlg-eyebrow{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9aa3b2;}
.mlg-avatar{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  font-size:12px;font-weight:700;color:#1b1430;background:linear-gradient(135deg,${GOLD_SOFT},${GOLD});flex:0 0 auto;
  box-shadow:0 0 0 3px rgba(232,185,107,.15);}
.mlg-title{font-size:22px;font-weight:700;line-height:1.18;color:#fff;letter-spacing:.2px;}
.mlg-fomo{font-size:12.5px;color:#cdb98f;display:flex;gap:6px;align-items:center;justify-content:center;text-align:center;}
.mlg-warm{font-size:12.5px;color:#aeb6c6;text-align:center;line-height:1.45;}
.mlg-input{width:100%;height:46px;border-radius:12px;background:rgba(255,255,255,.05);
  border:1px solid rgba(255,255,255,.12);color:#fff;font-size:15px;padding:0 14px;outline:none;transition:border-color .15s,box-shadow .15s;}
.mlg-input:focus{border-color:${GOLD};box-shadow:0 0 0 3px rgba(232,185,107,.14);}
.mlg-input.mlg-bad{border-color:#ff6b6b;box-shadow:0 0 0 3px rgba(255,107,107,.16);}
.mlg-otp{text-align:center;letter-spacing:.5em;font-size:20px;font-weight:600;padding-left:.5em;}
.mlg-cta{width:100%;height:48px;border:none;border-radius:12px;cursor:pointer;font-size:15px;font-weight:700;color:#1b1430;
  background:linear-gradient(135deg,${GOLD_SOFT},${GOLD});box-shadow:0 8px 22px -8px rgba(232,185,107,.7);
  transition:transform .1s,filter .15s;}
.mlg-cta:hover{filter:brightness(1.05);}
.mlg-cta:active{transform:translateY(1px);}
.mlg-cta:disabled{opacity:.55;cursor:default;filter:saturate(.6);}
.mlg-ghost{background:none;border:none;color:#9aa3b2;font-size:12.5px;cursor:pointer;padding:0;}
.mlg-ghost:hover:not(:disabled){color:#cfd5e3;}
.mlg-ghost:disabled{opacity:.5;cursor:default;}
.mlg-err{font-size:12.5px;color:#ff9b9b;text-align:center;}
.mlg-trust{font-size:11px;color:#8a93a5;display:flex;gap:5px;align-items:center;justify-content:center;}
.mlg-teaser{position:fixed;left:50%;bottom:22px;transform:translateX(-50%);z-index:2147482000;cursor:pointer;
  display:flex;align-items:center;gap:9px;max-width:90vw;padding:11px 16px;border-radius:999px;
  background:linear-gradient(180deg,rgba(33,30,66,.96),rgba(18,18,36,.97));color:#ece9f5;font-size:13px;
  border:1px solid rgba(232,185,107,.3);box-shadow:0 16px 40px -16px rgba(0,0,0,.6);
  animation:mlg-teaser-in .5s cubic-bezier(.18,.9,.32,1.2) both;}
.mlg-teaser b{color:#fff;}
.mlg-teaser .dot{width:7px;height:7px;border-radius:50%;background:${GOLD};flex:0 0 auto;box-shadow:0 0 10px ${GOLD};}
@keyframes mlg-fade{from{opacity:0;}to{opacity:1;}}
@keyframes mlg-pop{from{opacity:0;transform:translateY(20px) scale(.95);}to{opacity:1;transform:none;}}
@keyframes mlg-teaser-in{from{opacity:0;transform:translate(-50%,18px);}to{opacity:1;transform:translate(-50%,0);}}
.mlg-ring-track{stroke:rgba(255,255,255,.1);}
.mlg-ring-fill{stroke:url(#mlg-grad);stroke-linecap:round;transition:stroke-dashoffset .8s cubic-bezier(.2,.8,.2,1);}
.mlg-check-c{stroke:${GOLD};fill:none;stroke-width:5;stroke-dasharray:289;stroke-dashoffset:289;animation:mlg-draw .5s ease forwards;}
.mlg-check-t{stroke:#fff;fill:none;stroke-width:6;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:60;stroke-dashoffset:60;animation:mlg-draw .35s .42s ease forwards;}
@keyframes mlg-draw{to{stroke-dashoffset:0;}}
.mlg-burst{position:absolute;top:50%;left:50%;width:9px;height:9px;border-radius:2px;opacity:0;
  animation:mlg-fly .9s .15s ease-out forwards;}
@keyframes mlg-fly{0%{opacity:1;transform:translate(-50%,-50%) scale(1);}100%{opacity:0;transform:translate(var(--tx),var(--ty)) scale(.3) rotate(140deg);}}
.mlg-rise{animation:mlg-rise .5s .25s both;}
@keyframes mlg-rise{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:none;}}
/* Landscape phones: short viewport — compact the card and let it scroll. */
@media (max-height:480px){
  .mlg-card{padding:16px 18px 12px;}
  .mlg-title{font-size:18px;margin-bottom:8px!important;}
  .mlg-ring{transform:scale(.66);margin:-14px 0;}
  .mlg-warm,.mlg-trust{display:none;}
}
/* Wide landscape (most phones rotated): two columns — concierge | form — so
   it fits the short height without scrolling. */
@media (max-height:480px) and (min-width:640px){
  .mlg-card{max-width:680px;padding:18px 22px;}
  .mlg-grid{display:flex;gap:24px;align-items:center;}
  .mlg-hero,.mlg-body{flex:1 1 0;min-width:0;}
  .mlg-ring{transform:scale(.82);margin:6px 0;}
  .mlg-trust{display:flex;}
}

/* ── INSIDE AN EMBED, THE VIEWPORT IS THE IFRAME ──────────────────────
   This gate was built for a share page, where position:fixed means the whole
   screen. In an embed it means the 16:9 slot on someone else's page, and on a
   phone that slot is TINY — the height the card actually gets:

     iPhone SE / small Android   276 x 155
     iPhone 12/13/14             346 x 195
     Pixel / typical Android     368 x 207
     iPhone Pro Max              386 x 217
     iPad portrait               724 x 407

   So every phone lands far below the 480px floor the rules above assume, and
   the card — 26px padding, 22px title, two 46px inputs, a 48px button — turns
   into a scrolling sliver right at the moment we ask someone for their number.
   Tablets are fine and deliberately untouched.

   The approach is subtractive: at each step drop what is decoration and keep
   what is the transaction — title, inputs, button, errors. */

/* Portrait phone inside an embed — about 420px of height once the snippet's
   floor applies. Measured, the whole card is ~245px here, so this tier is NOT
   a stripped one: branding, the reassurance line and the trust row all stay,
   because they are what makes this read as the developer's own site asking
   rather than a bare form demanding a phone number.
   Only the progress ring and the FOMO counter go — they are the two biggest
   ornaments and they say nothing a buyer needs at this moment. */
@media (max-height:420px){
  .mlg-overlay{padding:10px;}
  .mlg-card{padding:16px 18px 14px;border-radius:18px;}
  .mlg-title{font-size:17px;line-height:1.24;margin-bottom:10px!important;}
  .mlg-logo{height:19px;}
  .mlg-warm{font-size:12px;line-height:1.4;}
  .mlg-ring,.mlg-fomo{display:none;}
  .mlg-input{height:42px;border-radius:10px;}
  .mlg-cta{height:44px;}
  .mlg-otp{letter-spacing:.34em;font-size:18px;padding-left:.34em;}
}

/* Short but WIDE — an iPad-portrait embed slot (~724x420). The two-column rule
   puts the hero on the left, and with the ring and the line both hidden that
   half is just a logo and a title beside the form. Bring the line back, left
   aligned under the title; the ring stays out, there is no height for it. */
@media (max-height:420px) and (min-height:321px) and (min-width:640px){
  .mlg-hero .mlg-fomo{display:flex;justify-content:flex-start;text-align:left;}
  .mlg-hero > div:last-child{align-items:flex-start!important;margin-bottom:0!important;}
}

/* Landscape phone: ~270px tall but wide, so the two-column rule below turns it
   into concierge | form. Still keep the trust row — a request for a phone
   number should never look anonymous, at any size.
   backdrop-filter goes here: a full-size blur is the most expensive thing on
   this overlay and low-end Android drops frames animating it in, and against a
   flat scrim at this size it reads the same. */
@media (max-height:320px){
  .mlg-overlay{padding:8px;background:rgba(8,11,20,.82);
    backdrop-filter:none;-webkit-backdrop-filter:none;}
  .mlg-card{padding:12px 14px;border-radius:16px;
    box-shadow:0 12px 30px -12px rgba(0,0,0,.7);animation-duration:.32s;}
  .mlg-title{font-size:15.5px;margin-bottom:7px!important;}
  .mlg-warm{display:none;}
  .mlg-input{height:39px;}
  .mlg-cta{height:41px;font-size:14.5px;}
  .mlg-err{font-size:11.5px;}
  .mlg-otp{letter-spacing:.28em;font-size:17px;padding-left:.28em;}
}

/* Last resort — a slot so short nothing decorative survives. Only reachable if
   a customer pasted an old snippet without the min-height floor, so it exists
   to stay usable rather than to look good. */
@media (max-height:240px){
  .mlg-card::before{display:none;}
  .mlg-logo,.mlg-eyebrow,.mlg-avatar,.mlg-trust{display:none;}
  .mlg-title{font-size:14.5px;margin-bottom:5px!important;}
  .mlg-input{height:36px;}
  .mlg-cta{height:38px;}
}

/* iOS zooms the page when a focused input is under 16px, and inside an iframe
   that zoom cannot be undone by the user — the embed is left scrolled into a
   corner of the host's page. 16px is the fix; it is a minimum, not a style. */
@media (max-width:480px){
  .mlg-input{font-size:16px;}
}

/* Narrow phones: the card is width:100% up to 380, so at 276px wide the only
   thing that overflows is the OTP field's letter-spacing. */
@media (max-width:360px){
  .mlg-card{border-radius:16px;}
  .mlg-otp{letter-spacing:.22em;padding-left:.22em;}
  .mlg-teaser{font-size:12px;padding:9px 13px;}
}

/* Notched phones, for the share page where this IS the full viewport. Harmless
   inside an iframe, where the insets resolve to zero. */
@supports (padding:max(0px)){
  .mlg-overlay{
    padding-left:max(10px,env(safe-area-inset-left));
    padding-right:max(10px,env(safe-area-inset-right));
    padding-bottom:max(10px,env(safe-area-inset-bottom));
  }
  .mlg-teaser{bottom:max(22px,calc(env(safe-area-inset-bottom) + 10px));}
}

/* Someone who asked not to be animated at should not be, least of all inside a
   page they did not choose to load. */
@media (prefers-reduced-motion:reduce){
  .mlg-overlay,.mlg-card,.mlg-teaser,.mlg-rise,.mlg-burst{animation:none!important;}
  .mlg-ring-fill{transition:none!important;}
}
`;

const ProgressRing: React.FC<{ value: number; total?: number }> = ({ value, total }) => {
  const r = 34;
  const c = 2 * Math.PI * r;
  const pct = total && total > 0 ? Math.min(value / total, 1) : 1;
  const label = total && total > 0 ? `${value}/${total}` : String(value);
  return (
    <svg width="84" height="84" viewBox="0 0 84 84">
      <defs>
        <linearGradient id="mlg-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={GOLD_SOFT} />
          <stop offset="1" stopColor={GOLD} />
        </linearGradient>
      </defs>
      <circle className="mlg-ring-track" cx="42" cy="42" r={r} fill="none" strokeWidth="6" />
      <circle
        className="mlg-ring-fill"
        cx="42"
        cy="42"
        r={r}
        fill="none"
        strokeWidth="6"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 42 42)"
      />
      <text x="42" y="40" textAnchor="middle" fontSize="18" fontWeight="700" fill="#fff">
        {label}
      </text>
      <text x="42" y="55" textAnchor="middle" fontSize="9" fill="#9aa3b2" letterSpacing="1">
        SPACES
      </text>
    </svg>
  );
};

const CONFETTI = Array.from({ length: 12 }, (_, i) => {
  const angle = (i / 12) * Math.PI * 2;
  const dist = 70 + (i % 3) * 14;
  const colors = [GOLD, GOLD_SOFT, "#fff", "#b9a7ff"];
  return {
    tx: `calc(-50% + ${Math.round(Math.cos(angle) * dist)}px)`,
    ty: `calc(-50% + ${Math.round(Math.sin(angle) * dist)}px)`,
    color: colors[i % colors.length],
  };
});

const shake = (el: HTMLElement | null) => {
  try {
    el?.animate?.(
      [
        { transform: "translateX(0)" },
        { transform: "translateX(-8px)" },
        { transform: "translateX(7px)" },
        { transform: "translateX(-5px)" },
        { transform: "translateX(4px)" },
        { transform: "translateX(0)" },
      ],
      { duration: 380, easing: "ease-in-out" },
    );
  } catch {
    /* WAAPI not available — no-op */
  }
};

const MatterportLeadGate: React.FC<MatterportLeadGateProps> = ({
  enabled,
  agencyId,
  projectKey,
  projectName,
  sharerName,
  agencyName,
  agencyLogoUrl,
  sessionId,
  embedKey,
  utm,
  sweepCount,
  totalSpaces,
  nodeTarget = 3,
  minDwellMs = 10_000,
  maxWaitMs = 30_000,
  armOnMount = false,
  onUnlock,
}) => {
  const [shown, setShown] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [demo, setDemo] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [badPhone, setBadPhone] = useState(false);
  const [badEmail, setBadEmail] = useState(false);
  const [badOtp, setBadOtp] = useState(false);

  const deviceId = useMemo(() => getDeviceId(), []);
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const teaserDoneRef = useRef(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const onUnlockRef = useRef(onUnlock);
  onUnlockRef.current = onUnlock;

  const ctx: LeadCtx = useMemo(
    () => ({ agencyId, deviceId, sessionId, embedKey, utm, countryCode: "+91" }),
    [agencyId, deviceId, sessionId, embedKey, utm],
  );

  // Mount: honour an existing unlock (skip the gate entirely).
  useEffect(() => {
    if (!enabled) return;
    const rec = getActiveUnlock(projectKey);
    if (rec) {
      setUnlocked(true);
      onUnlockRef.current?.({ customerId: rec.customerId });
    }
  }, [enabled, projectKey]);

  // Arm only once the experience is actually LIVE — the VR session has started
  // (model PLAYING) or the first sweep landed — never at mount. Otherwise the
  // dwell / fallback timers burn down during the (~20-30s) Matterport load and
  // the gate pops with 0 sweeps the instant it finishes loading.
  //
  // `armOnMount` is the escape hatch for an experience that gives us NEITHER
  // signal: a meta model in a cross-origin iframe starts its own session and
  // emits no sweeps, so both conditions stay false forever and the gate would
  // never arm at all. Callers using it own the wait themselves via maxWaitMs,
  // which is why it can afford to be generous.
  useEffect(() => {
    if (enabled && !unlocked && armedAt == null && (armOnMount || !!sessionId || sweepCount > 0)) {
      setArmedAt(Date.now());
    }
  }, [enabled, unlocked, armedAt, sessionId, sweepCount, armOnMount]);

  // Anticipation pre-cue: a warm, dismissible teaser the moment they start
  // moving — so the gate is expected, never a cold pop-up. Shows once.
  useEffect(() => {
    if (!enabled || unlocked || shown || teaserDoneRef.current || sweepCount < 1) return undefined;
    teaserDoneRef.current = true;
    setTeaser(true);
    const t = setTimeout(() => setTeaser(false), 6000);
    return () => clearTimeout(t);
  }, [enabled, unlocked, shown, sweepCount]);

  // Trigger: sweeps>=target (after min dwell) OR the time fallback.
  useEffect(() => {
    if (!enabled || shown || unlocked) return undefined;
    const { show, delayMs } = evaluateTrigger({
      sweepCount,
      armedAt,
      now: Date.now(),
      nodeTarget,
      minDwellMs,
      maxWaitMs,
    });
    if (delayMs < 0) return undefined; // not armed yet
    if (show) {
      setTeaser(false);
      setShown(true);
      return undefined;
    }
    const t = setTimeout(() => {
      setTeaser(false);
      setShown(true);
    }, delayMs);
    return () => clearTimeout(t);
  }, [sweepCount, enabled, shown, unlocked, armedAt, nodeTarget, minDwellMs, maxWaitMs]);

  // Resend cooldown countdown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const startOtpStep = useCallback(() => {
    setOtp("");
    setBadOtp(false);
    setStep("otp");
    setResendIn(RESEND_COOLDOWN_S);
  }, []);

  const finishUnlock = useCallback(
    (customerId: string) => {
      writeUnlock(projectKey, customerId);
      setStep("success");
      window.setTimeout(() => setLeaving(true), 2000);
      window.setTimeout(() => {
        setUnlocked(true);
        setShown(false);
        onUnlockRef.current?.({ customerId });
      }, 2550);
    },
    [projectKey],
  );

  const submitPhone = useCallback(async () => {
    setError("");
    const invalidPhone = validatePhone(phone);
    if (invalidPhone) {
      setError(invalidPhone);
      setBadPhone(true);
      shake(phoneRef.current);
      return;
    }
    const invalidEmail = validateEmail(email);
    if (invalidEmail) {
      setError(invalidEmail);
      setBadEmail(true);
      shake(emailRef.current);
      return;
    }
    setBusy(true);
    // check-status keys on BOTH mobile + email (strict identity lookup) — so
    // both are needed up front to avoid a false 409 conflict on an existing lead.
    const r = await checkLeadStatus(ctx, { mobile: phone, email });
    if (r.demo) setDemo(true);
    const outcome = interpretCheckStatus(r);
    if (outcome.kind === "conflict") {
      setBusy(false);
      setError("Looks like this number's already with us under different details. Try another?");
      setBadPhone(true);
      shake(phoneRef.current);
      return;
    }
    if (outcome.kind === "error") {
      setBusy(false);
      setError(outcome.message);
      return;
    }
    if (outcome.kind === "unlock") {
      setBusy(false);
      finishUnlock(outcome.customerId);
      return;
    }
    if (outcome.kind === "otp") {
      const s = await sendLeadOtp(ctx, { mobile: phone, email });
      setBusy(false);
      if (s.demo) setDemo(true);
      if (!s.ok) {
        setError(s.error || "Couldn't send the code just now — try once more?");
        return;
      }
      startOtpStep();
      return;
    }
    setBusy(false);
    setStep("name");
  }, [phone, email, ctx, finishUnlock, startOtpStep]);

  const submitName = useCallback(async () => {
    setError("");
    const invalid = validateName(firstName);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    const s = await sendLeadOtp(ctx, { mobile: phone, firstName, lastName, email });
    setBusy(false);
    if (s.demo) setDemo(true);
    if (!s.ok) {
      setError(s.error || "Couldn't send the code just now — try once more?");
      return;
    }
    startOtpStep();
  }, [firstName, lastName, phone, email, ctx, startOtpStep]);

  const submitOtp = useCallback(async () => {
    setError("");
    const invalid = validateOtp(otp);
    if (invalid) {
      setError(invalid);
      setBadOtp(true);
      shake(otpRef.current);
      return;
    }
    setBusy(true);
    const v = await verifyLeadOtp(ctx, { mobile: phone, otp, firstName, lastName, email });
    setBusy(false);
    if (v.demo) setDemo(true);
    const outcome = interpretVerify(v);
    if (outcome.kind === "error") {
      setError(outcome.message);
      setBadOtp(true);
      shake(otpRef.current);
      return;
    }
    finishUnlock(outcome.customerId);
  }, [otp, phone, firstName, lastName, email, ctx, finishUnlock]);

  const resendOtp = useCallback(async () => {
    if (resendIn > 0 || busy) return;
    setError("");
    setBusy(true);
    const s = await sendLeadOtp(ctx, { mobile: phone, firstName, lastName, email });
    setBusy(false);
    if (s.demo) setDemo(true);
    if (!s.ok) {
      setError(s.error || "Couldn't resend just now — try once more?");
      return;
    }
    setOtp("");
    setBadOtp(false);
    setResendIn(RESEND_COOLDOWN_S);
  }, [resendIn, busy, ctx, phone, firstName, lastName, email]);

  const changeNumber = useCallback(() => {
    setError("");
    setOtp("");
    setBadOtp(false);
    setResendIn(0);
    setStep("phone");
  }, []);

  if (!enabled || unlocked) return null;

  // Pre-gate: only the warm teaser (if any) — the experience stays fully open.
  if (!shown) {
    return teaser ? (
      <div className="mlg-teaser" onClick={() => setTeaser(false)} role="button">
        <style>{STYLE}</style>
        <span className="dot" aria-hidden />
        <span>
          {sharerName ? (
            <>
              <b>{sharerName}</b> has the full tour ready for you
            </>
          ) : (
            <>The full walkthrough is coming up — enjoy looking around</>
          )}
        </span>
      </div>
    ) : null;
  }

  const initials = (sharerName || agencyName || "OS")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  // The ring is progress, so it only shows once there IS progress to show: a
  // meta model reports no spaces at all, and a gate opened by the timer before
  // the visitor moved would read "0/116". Both get the line alone instead.
  const showRing = !!totalSpaces && totalSpaces > 0 && sweepCount > 0;
  const fomoLine = !totalSpaces
    ? "Unlock the full walkthrough"
    : sweepCount <= 0
      ? `Unlock the full walkthrough — all ${totalSpaces} spaces`
      : totalSpaces > sweepCount
        ? `Unlock the full walkthrough — ${totalSpaces - sweepCount} more spaces to explore`
        : "Unlock the full walkthrough — every space to explore";
  const ctaLabel = busy
    ? "One moment…"
    : step === "phone"
      ? "Step inside →"
      : step === "name"
        ? "Continue →"
        : "Verify & enter →";
  const onCta = step === "phone" ? submitPhone : step === "name" ? submitName : submitOtp;

  return (
    <div className={`mlg-overlay${leaving ? " mlg-leaving" : ""}`} role="dialog" aria-modal="true">
      <style>{STYLE}</style>
      <div className="mlg-card">
        {step !== "success" ? (
          <div className="mlg-grid">
            <div className="mlg-hero">
            {/* Concierge frame */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              {agencyLogoUrl ? (
                <img className="mlg-logo" src={agencyLogoUrl} alt={agencyName || "Agency"} />
              ) : (
                <span className="mlg-eyebrow">{agencyName || "OpenSky"}</span>
              )}
              <span className="mlg-eyebrow">By invitation</span>
            </div>

            {sharerName && (
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 12 }}>
                <span className="mlg-avatar">{initials}</span>
                <div style={{ fontSize: 12.5, color: "#aeb6c6", lineHeight: 1.35 }}>
                  <b style={{ color: "#fff" }}>{sharerName}</b> personally invited you to explore
                </div>
              </div>
            )}

            <div className="mlg-title" style={{ marginBottom: 14 }}>
              {projectName || "this home"}
            </div>

            {/* Progress hero + warm FOMO accent */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginBottom: 16 }}>
              {showRing && (
                <div className="mlg-ring">
                  <ProgressRing value={sweepCount} total={totalSpaces} />
                </div>
              )}
              <div className="mlg-fomo">
                <span aria-hidden>✨</span>
                {fomoLine}
              </div>
            </div>
            </div>

            <div className="mlg-body">
            {/* Adaptive fields */}
            {step === "phone" && (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div
                    style={{
                      height: 46,
                      display: "flex",
                      alignItems: "center",
                      padding: "0 14px",
                      borderRadius: 12,
                      background: "rgba(255,255,255,.05)",
                      border: "1px solid rgba(255,255,255,.12)",
                      fontSize: 15,
                      color: "#cfd5e3",
                      whiteSpace: "nowrap",
                    }}
                  >
                    +91
                  </div>
                  <input
                    ref={phoneRef}
                    className={`mlg-input${badPhone ? " mlg-bad" : ""}`}
                    inputMode="numeric"
                    placeholder="Your phone number"
                    value={phone}
                    maxLength={10}
                    autoFocus
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D+/g, ""));
                      if (badPhone) setBadPhone(false);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && !busy && submitPhone()}
                  />
                </div>
                <input
                  ref={emailRef}
                  className={`mlg-input${badEmail ? " mlg-bad" : ""}`}
                  type="email"
                  inputMode="email"
                  placeholder="Your email"
                  value={email}
                  style={{ marginBottom: 8 }}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (badEmail) setBadEmail(false);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && !busy && submitPhone()}
                />
                <div className="mlg-warm" style={{ marginBottom: 12 }}>
                  A quick verification, then the entire experience is yours to explore.
                </div>
              </>
            )}

            {step === "name" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input
                  className="mlg-input"
                  placeholder="First name"
                  value={firstName}
                  autoFocus
                  onChange={(e) => setFirstName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && submitName()}
                />
                <input
                  className="mlg-input"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && submitName()}
                />
              </div>
            )}

            {step === "otp" && (
              <div style={{ marginBottom: 12 }}>
                <input
                  ref={otpRef}
                  className={`mlg-input mlg-otp${badOtp ? " mlg-bad" : ""}`}
                  inputMode="numeric"
                  placeholder="······"
                  value={otp}
                  maxLength={6}
                  autoFocus
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D+/g, ""));
                    if (badOtp) setBadOtp(false);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && !busy && submitOtp()}
                />
                <div style={{ marginTop: 6, textAlign: "center" }}>
                  <span style={{ fontSize: 11.5, color: "#8a93a5" }}>
                    We just texted a code to +91 {phone}
                    {demo ? "  ·  demo: use 123456" : ""}
                  </span>
                </div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button className="mlg-ghost" onClick={changeNumber} disabled={busy}>
                    ← Change number
                  </button>
                  <button className="mlg-ghost" onClick={resendOtp} disabled={busy || resendIn > 0}>
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </div>
            )}

            {error && <div className="mlg-err" style={{ marginBottom: 10 }}>{error}</div>}

            <button className="mlg-cta" disabled={busy} onClick={onCta}>
              {ctaLabel}
            </button>

            <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
              <span className="mlg-trust">
                <span aria-hidden>🔒</span> Only used to connect you with {agencyName || "the team"} — never any spam. 💛
              </span>
            </div>
            </div>
          </div>
        ) : (
          /* "Earned it" success beat */
          <div style={{ position: "relative", textAlign: "center", padding: "8px 0 4px" }}>
            <div style={{ position: "relative", width: 96, height: 96, margin: "0 auto 14px" }}>
              {CONFETTI.map((c, i) => (
                <span
                  key={i}
                  className="mlg-burst"
                  style={{ background: c.color, ["--tx" as any]: c.tx, ["--ty" as any]: c.ty }}
                />
              ))}
              <svg width="96" height="96" viewBox="0 0 96 96">
                <circle className="mlg-check-c" cx="48" cy="48" r="44" />
                <path className="mlg-check-t" d="M30 49 L43 62 L66 35" />
              </svg>
            </div>
            <div className="mlg-rise" style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
              {firstName ? `So glad you're here, ${firstName}! 🎉` : "So glad you're here! 🎉"}
            </div>
            <div className="mlg-rise" style={{ marginTop: 6, fontSize: 13, color: "#aeb6c6" }}>
              Enjoy every corner of {projectName || "this home"}.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatterportLeadGate;
